import { useCallback, useContext } from 'react';
import { enqueueSnackbar } from 'notistack';
import uuid from 'react-uuid';
import { COMPANY_TYPE, FIRM_TYPE, FUND_TYPE } from 'common/constants/pageTypes';
import { ExcelExportContextValues } from 'common/types/store';
import ExcelExportContext from 'context/ExcelExportContext';
import {
  ExcelExportFormValues,
  GenerateReportRequestData,
  UseGenerateReportsProps,
} from 'layouts/Main/components/ExcelExportDialog/types';
import { useGetInvestorAppReport } from 'services/hooks';
import { extractServerErrorMessage, getArrayValue } from 'utilities';
import parseFundByMeasurementDateData from './parseFundByMeasurementDateData';

// Constants
const ERROR_TOAST_DURATION_MS = 5000;

const useGenerateReports = (params: UseGenerateReportsProps) => {
  const {
    pageType,
    firmInfo,
    companyInfo,
    fundInfo,
    currentMeasurementDate,
    setErrors,
    formValues,
    waterfallParameters,
  } = params;
  const beginReportProcess = useGetInvestorAppReport();
  const { setDoReportPollCheck, setReportRequestData, setReportUUID, fundsByMeasurementDate, isLoading } = useContext(
    ExcelExportContext
  ) as unknown as ExcelExportContextValues;

  const generateReport = useCallback(
    async (reportFormValues: Partial<ExcelExportFormValues>) => {
      const parsedFundByMeasurementDateData =
        pageType === FIRM_TYPE ? parseFundByMeasurementDateData(getArrayValue(fundsByMeasurementDate)) : [];

      const requestData: GenerateReportRequestData = {
        ...reportFormValues,
        export_not_formulated: formValues.export_not_formulated,
        export_print_ready: formValues.export_formulated_and_print_ready,
        company: pageType === COMPANY_TYPE ? companyInfo?.id : null,
        firm: pageType === FIRM_TYPE ? firmInfo?.id : null,
        fund: pageType === FUND_TYPE ? fundInfo?.id : null,
        measurement_date: currentMeasurementDate?.id ?? null,
        funds_by_measurement_date: parsedFundByMeasurementDateData,
        include_documents_and_notes_from_tabs: formValues.include_documents_and_notes_from_tabs,
      };

      const reportUUID = uuid();

      setReportRequestData(requestData);
      setReportUUID(reportUUID);
      setDoReportPollCheck(true);

      const reportErrors = await beginReportProcess(reportUUID, requestData);
      if (reportErrors) {
        setErrors(reportErrors);
        // stop polling to avoid multiple requests
        setDoReportPollCheck(false);
        // Clear any polling state that might have been set
        setReportRequestData(null);
        setReportUUID(null);

        // Show server error details in toast if available
        const serverErrorMessage = extractServerErrorMessage(reportErrors);
        if (serverErrorMessage) {
          enqueueSnackbar(serverErrorMessage, {
            variant: 'error',
            autoHideDuration: ERROR_TOAST_DURATION_MS,
          });
        }

        // Throw an error to trigger retry path
        throw new Error('Export failed');
      }
      // Don't return true immediately - let polling handle the completion
      // The dialog should stay open until polling completes or fails
    },
    [
      beginReportProcess,
      companyInfo?.id,
      currentMeasurementDate?.id,
      firmInfo?.id,
      formValues,
      fundInfo?.id,
      fundsByMeasurementDate,
      pageType,
      setReportRequestData,
      setErrors,
      setReportUUID,
      setDoReportPollCheck,
    ]
  );

  const generateCompanySummaryReport = useCallback(() => generateReport({ export_summary: true }), [generateReport]);
  const generateCapTablesReport = useCallback(
    () => generateReport({ export_cap_table: true, cap_table: formValues.cap_table }),
    [generateReport, formValues]
  );
  const generateFundOwnershipReport = useCallback(
    () => generateReport({ export_fund_ownership: true, fo_cap_table: formValues.fo_cap_table }),
    [generateReport, formValues]
  );
  const generateBreakpointReport = useCallback(
    () => generateReport({ export_breakpoints: true, breakpoint_cap_table: formValues.breakpoint_cap_table }),
    [generateReport, formValues]
  );
  const generateWaterfallReport = useCallback(
    () =>
      generateReport({
        export_waterfall: true,
        waterfall_cap_table: formValues.waterfall_cap_table,
        waterfall_parameters: waterfallParameters,
      }),
    [generateReport, formValues, waterfallParameters]
  );
  const generateFinancialsReport = useCallback(
    () => generateReport({ export_financials: true, financial_statement: formValues.financial_statement }),
    [generateReport, formValues]
  );
  const generateValuationsReport = useCallback(
    () => generateReport({ export_valuations: true, valuation: formValues.valuation }),
    [generateReport, formValues]
  );
  const generateFirmSummaryReport = useCallback(
    () =>
      generateReport({
        export_firm_summary: true,
        export_firm_funds_summaries: true,
        report_date: formValues.report_date,
      }),
    [generateReport, formValues]
  );
  const generateFundSummaryReport = useCallback(() => generateReport({ export_fund_summary: true }), [generateReport]);
  const generateFundInvestmentsReport = useCallback(
    () => generateReport({ export_fund_investments: true }),
    [generateReport]
  );
  const generateDocumentsReport = useCallback(() => generateReport({ export_documents: true }), [generateReport]);

  return {
    generateReport,
    generateCompanySummaryReport,
    generateCapTablesReport,
    generateFundOwnershipReport,
    generateBreakpointReport,
    generateWaterfallReport,
    generateFinancialsReport,
    generateValuationsReport,
    generateFirmSummaryReport,
    generateFundSummaryReport,
    generateFundInvestmentsReport,
    generateDocumentsReport,
    isLoading,
  };
};

export default useGenerateReports;
